import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react";
import userDetail from "Stores/userDetail";
import DropDownMenu from "Components/common/DropDownMenu";
import { updateContactOwnerState } from "Components/common/UpdateContactOwner";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import Utils from "Utils/utils";
import { ReactComponent as UpdateIcon } from "Assets/svg/update.svg";
import { accountsState } from "./AccountsState";
import { accountBulkActionsState } from "./AccountBulkActionsState";
import { editAccountPopupState } from "./AccountInfoCard/EditAccountInfoPopup";
import AddAccountsToList from "./AddAccountsToList";

const AccountRowAction = observer((props = {}) => {
  const {
    key = "",
    referenceId = "",
    tableLoading = false,
    rowData = {},
    reloadAccountCard = () => {}
  } = props || {};

  const history = useHistory();
  const [showRowActionsDropdown, setShowRowActionsDropdown] = useState(false);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [savingStage, setSavingStage] = useState(false);
  const [triggerAddToListCounter, setTriggerAddToListCounter] = useState(0);

  const closeMenus = () => {
    setShowRowActionsDropdown(false);
    setShowStageDropdown(false);
  };

  const expandActionMenu = (id = "", e) => {
    if (!tableLoading) {
      Utils.preventDefaultFn(e);
      setShowStageDropdown(false);
      setShowRowActionsDropdown(referenceId === id);
    }
  };

  const singleOwnerUpdatePayload = (ownerId = "") => {
    if (rowData?.id) {
      return {
        accountIds: [rowData?.id || ""],
        ownerId
      };
    }
    return false;
  };

  const AccountOwnerSingleUpdate = async (ownerId = "") => {
    const data = singleOwnerUpdatePayload(ownerId);
    if (data) {
      const config = {
        url: URLS.updateAccountOwner,
        method: "POST",
        data
      };
      const response = await makeApi(config);
      updateContactOwnerState.setBtnLoader(false);
      if (response?.data?.successCount > 0) {
        toasterState.setToastMsg(
          `Account owner updated successfully`,
          "success"
        );
        reloadAccountCard();
      } else if (response?.response?.status === 401) {
        toasterState.setToastMsg(
          "Oops! You don't have access to update the account owner.",
          "fail"
        );
      } else {
        toasterState.setToastMsg(
          "Something went wrong. Please try again later",
          "fail"
        );
      }
    }
  };

  const openAssignAccountOwnerPopup = () => {
    updateContactOwnerState.setPopupType("ACCOUNT");
    updateContactOwnerState.setCurrentOwnerEmail(rowData?.owner?.email || "");
    updateContactOwnerState.setTotalCount(1);
    updateContactOwnerState.setCallback(AccountOwnerSingleUpdate);
    updateContactOwnerState.setShowPopup(true);
    closeMenus();
  };

  // Mirrors the per-row Stage column's own update (ListStageChip in
  // AccountsTable.js) — same endpoint, kept as a separate small submenu here
  // (rather than reaching into that component's local state) since the two
  // triggers (table cell vs row menu) aren't wired to share state.
  const handleSelectStage = async (stageId = "", stageName = "") => {
    if (!rowData?.id || savingStage || stageName === rowData?.stage) {
      setShowStageDropdown(false);
      return;
    }
    setSavingStage(true);
    setShowStageDropdown(false);
    const response = await makeApi({
      url: URLS.updateAccountStage,
      method: "POST",
      data: { stageId, accountId: rowData.id }
    });
    setSavingStage(false);
    if (+response?.status === 200) {
      toasterState.setToastMsg("Account stage updated", "success");
      reloadAccountCard();
    } else {
      toasterState.setToastMsg("Failed to update stage", "failure");
    }
  };

  const stageOptions = (accountsState?.accountStages || [])
    .filter(s => s?.displayName)
    .map(s => ({
      value: s.displayName,
      key: s.id,
      method: () => handleSelectStage(s.id, s.displayName)
    }));

  const openUpdateStage = () => {
    setShowStageDropdown(!showStageDropdown);
  };

  const openAddToList = () => {
    setShowRowActionsDropdown(false);
    setTriggerAddToListCounter(c => c + 1);
  };

  // Single-account add-to-list — same bulk-operation endpoint the toolbar's
  // multi-select "Add to List" uses, scoped to just this row's id. A single
  // account never crosses the background-job threshold, so (unlike the
  // toolbar version) this skips the background/active-job branches.
  const addToListAction = async (listId = "", cbk = () => {}) => {
    const response = await makeApi({
      url: URLS.performBulkAccountOperation,
      method: "POST",
      data: {
        type: "ADD_TO_ACCOUNT_LIST",
        totalCount: 1,
        filter: { accountIds: [rowData?.id] },
        data: { listId }
      }
    });
    cbk();
    const { success = 0 } = response?.data?.bulkOperationResult || {};
    if (+response?.status === 200 && success > 0) {
      toasterState.setToastMsg("Account added to list", "success");
      accountsState.fetchAccountLists();
      reloadAccountCard();
    } else {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    }
  };

  const openEditAccount = () => {
    closeMenus();
    editAccountPopupState.setAccount(rowData || {});
    editAccountPopupState.setShowPopup(true);
  };

  const viewAccount = () => {
    closeMenus();
    rowData?.id && history.push(`/accounts/${rowData.id}/overview`);
  };

  // Single-account delete — same bulk-operation endpoint/type the toolbar's
  // multi-select "Delete" uses, scoped to just this row's id.
  const runDeleteAccount = async () => {
    const response = await accountBulkActionsState.deleteAccounts(
      [rowData?.id],
      1
    );
    confirmationPopupState.setShowPopup(false);
    const { success = 0, failed = 0 } =
      response?.data?.bulkOperationResult || {};
    if (+response?.status === 200 && success > 0) {
      toasterState.setToastMsg("Account deleted successfully", "success");
      reloadAccountCard();
    } else if (failed > 0) {
      toasterState.setToastMsg(
        "Couldn't delete this account — it may have contacts, or you may not have permission to delete it",
        "fail"
      );
    } else {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    }
  };

  const openDeleteAccountConfirm = () => {
    closeMenus();
    confirmationPopupState.setPopupValues({
      content:
        "Are you sure you want to delete this account? Accounts that still have contacts can't be deleted.",
      actionBtnText: "Delete",
      cancelBtnText: "Cancel",
      loadingMsg: "Deleting...",
      cancelCbk: () => confirmationPopupState.setShowPopup(false),
      callback: runDeleteAccount
    });
    confirmationPopupState.setShowPopup(true);
  };

  const { hasTeamMember = false } = userDetail?.userInfo || {};

  const menuOptions = [
    {
      icon: { ele: <UpdateIcon />, style: { width: "15px", height: "15px" } },
      value: "Update Stage",
      key: "updateAccountStage",
      method: openUpdateStage
    },
    hasTeamMember && {
      icon: {
        ele: '<i style="font-size: 15px" class="material-icons-outlined">person_pin</i>',
        style: { width: "15px", height: "15px" }
      },
      value: "Update Owner",
      key: "updateAccountOwner",
      method: openAssignAccountOwnerPopup
    },
    {
      icon: {
        ele: '<i style="font-size: 15px" class="material-icons-outlined">playlist_add</i>',
        style: { width: "15px", height: "15px" }
      },
      value: "Add to List",
      key: "addAccountToList",
      method: openAddToList
    },
    {
      icon: {
        ele: '<i style="font-size: 15px" class="material-icons-outlined list-icon">edit</i>',
        style: { width: "15px", height: "15px" }
      },
      value: "Edit",
      key: "editAccount",
      method: openEditAccount
    },
    {
      icon: {
        ele: '<i style="font-size: 15px" class="material-icons-outlined">open_in_new</i>',
        style: { width: "15px", height: "15px" }
      },
      value: "View Account",
      key: "viewAccount",
      method: viewAccount
    },
    {
      icon: {
        ele: '<i style="font-size: 15px" class="material-icons-outlined">delete</i>',
        style: { width: "15px", height: "15px" }
      },
      value: "Delete",
      key: "deleteAccount",
      method: openDeleteAccountConfirm
    }
  ].filter(Boolean);

  return (
    <div className="actionDp moreOption" key={key}>
      <i
        className="material-icons-outlined icon-hover-btn enabled"
        onClick={e => expandActionMenu(rowData?.id, e)}
      >
        more_vert
      </i>
      {showRowActionsDropdown && (
        <div className="accountDp">
          <DropDownMenu
            options={menuOptions}
            referenceId={referenceId}
            direction={key >= 0 && key <= 1 ? "topLeft" : "left"}
          />
          {showStageDropdown && (
            <DropDownMenu
              options={stageOptions}
              referenceId={referenceId}
              direction="bottomUp"
            />
          )}
          <div
            className="dropdownMask"
            onClick={event => {
              Utils.preventDefaultFn(event);
              closeMenus();
            }}
          />
        </div>
      )}
      <AddAccountsToList
        accountIds={rowData?.id ? [rowData.id] : []}
        loading={tableLoading}
        hidden={true}
        trigger={triggerAddToListCounter}
        onAddToList={addToListAction}
      />
    </div>
  );
});

export default AccountRowAction;
export { AccountRowAction };
